/**
 * Loki 로그 전송 유틸리티
 */
import useAppStore from '@store/useAppStore';

import PackageJson from '../../../package.json';

class LokiLogger {
  private config: LokiLoggerConfig;
  private buffer: LokiStream[] = [];
  private flushTimer: NodeJS.Timeout | null = null;

  constructor(config: Partial<LokiLoggerConfig> = {}) {
    this.config = {
      appName: process.env.REACT_APP_LOKI_APP_NAME,
      service: process.env.REACT_APP_LOKI_SERVICE,
      lokiUrl: process.env.REACT_APP_LOKI_URL,
      environment: process.env.REACT_APP_LOKI_ENVIRONMENT || 'production',
      enabled: true, // 기본적으로 활성화
      batchSize: 10, // 10개씩 배치 전송
      flushInterval: 5000, // 5초마다 자동 flush
      ...config,
    };

    // 자동 flush 타이머 시작
    if (this.config.enabled) {
      this.startFlushTimer();
    }
  }

  /**
   * 나노초 타임스탬프 생성 (Loki 형식)
   */
  private getNanoTimestamp(): string {
    const now = Date.now();
    const nanos = now * 1000000; // 밀리초를 나노초로 변환
    return nanos.toString();
  }

  /**
   * 로그 메시지 포맷팅
   */
  private formatMessage(level: LogLevel, message: string, metadata?: Record<string, any>): string {
    const parts = [`[${level.toUpperCase()}]`, message];

    if (metadata && Object.keys(metadata).length > 0) {
      parts.push(JSON.stringify(metadata));
    }

    return parts.join(' ');
  }

  /**
   * Loki로 로그 전송
   */
  private async sendToLoki(payload: LokiPayload): Promise<void> {
    if (!this.config.enabled) {
      return;
    }

    try {
      const response = await fetch(this.config.lokiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error('[LokiLogger] 전송 실패:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('[LokiLogger] 전송 에러:', error);
    }
  }

  /**
   * 버퍼에 로그 추가
   */
  private addToBuffer(level: LogLevel, message: string, metadata?: Record<string, any>): void {
    const merchantInfo = this.config.getMerchantInfo ? this.config.getMerchantInfo() : {};
    const userInfo = this.config.getUserInfo ? this.config.getUserInfo() : {};
    const mergedData = { ...merchantInfo, ...userInfo };

    const stream: LokiStream = {
      stream: {
        app: this.config.appName,
        version: PackageJson.version,
        level,
        environment: this.config.environment,
        service: this.config.service,
        ...mergedData,
      },
      values: [[this.getNanoTimestamp(), this.formatMessage(level, message, metadata)]],
    };

    this.buffer.push(stream);

    // 버퍼 크기가 batchSize에 도달하면 즉시 flush
    if (this.buffer.length >= this.config.batchSize) {
      this.flush();
    }
  }

  /**
   * 버퍼의 로그를 Loki로 전송
   */
  public async flush(): Promise<void> {
    if (this.buffer.length === 0) {
      return;
    }

    const payload: LokiPayload = {
      streams: [...this.buffer],
    };

    this.buffer = [];

    await this.sendToLoki(payload);
  }

  /**
   * 자동 flush 타이머 시작
   */
  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.config.flushInterval);
  }

  /**
   * 로거 종료 (페이지 언마운트 시 호출)
   */
  public async destroy(): Promise<void> {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // 남은 버퍼 전송
    await this.flush();
  }

  /**
   * prefix를 포함한 메시지 생성
   */
  private formatWithPrefix(prefix: string | undefined, message: string): string {
    return prefix ? `[${prefix}] ${message}` : message;
  }

  // 로그 레벨별 메서드
  public debug(message: string, metadata?: Record<string, any>, prefix?: string): void {
    this.addToBuffer('debug', this.formatWithPrefix(prefix, message), metadata);
  }

  public info(message: string, metadata?: Record<string, any>, prefix?: string): void {
    this.addToBuffer('info', this.formatWithPrefix(prefix, message), metadata);
  }

  public warn(message: string, metadata?: Record<string, any>, prefix?: string): void {
    this.addToBuffer('warn', this.formatWithPrefix(prefix, message), metadata);
  }

  public error(message: string, metadata?: Record<string, any>, prefix?: string): void {
    this.addToBuffer('error', this.formatWithPrefix(prefix, message), metadata);
    // 에러는 즉시 전송
    this.flush();
  }
}

/**
 * 지점 정보 가져오기 (Zustand store에서)
 */
const getMerchantInfo = (): { merchantId?: string; merchantName?: string } => {
  try {
    const merchantData = useAppStore.getState().merchantData;
    return {
      merchantId: merchantData?.merchantId?.toString() || merchantData?.id?.toString(),
      merchantName: merchantData?.name || 'unknown',
    };
  } catch (error) {
    return { merchantName: 'unknown' };
  }
};

/**
 * 사용자 정보 가져오기 (Zustand store에서)
 */
const getUserInfo = (): { merchantId?: string; userId?: string; phoneNum?: string } => {
  try {
    const merchantData = useAppStore.getState().merchantData;
    return {
      merchantId: merchantData?.merchantId?.toString() || merchantData?.id?.toString(),
      userId: merchantData?.userId?.toString(),
      phoneNum: merchantData?.phoneNum,
    };
  } catch (error) {
    return { userId: 'unknown' };
  }
};

/**
 * 기본 Loki 로거 인스턴스 (사용자 정보 미포함)
 */
const lokiLogger = new LokiLogger({
  appName: process.env.REACT_APP_LOKI_APP_NAME,
  service: process.env.REACT_APP_LOKI_SERVICE,
  getMerchantInfo, // 지점 정보 자동 추가
});

/**
 * 사용자 Loki 로거 인스턴스 (merchantId, userId, phoneNum 포함)
 */
export const userLokiLogger = new LokiLogger({
  appName: process.env.REACT_APP_LOKI_APP_NAME,
  service: process.env.REACT_APP_LOKI_SERVICE,
  getUserInfo, // 사용자 정보 자동 추가
});

/**
 * 페이지 언마운트 시 로거 정리
 */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    lokiLogger.destroy();
    userLokiLogger.destroy();
  });
}

export default lokiLogger;
